import { memo, useCallback } from 'react';

import {
  WORKSPACE_AGENT_MAX_NUMBERED_OPTIONS,
  workspaceAgentChoiceChip,
  workspaceAgentChoiceChipNumber,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentChoiceChip';
import { keepWorkspaceAgentDockFocusOnPointer } from '~/components/v2/components/workspace/workspace-agent-panel/utils/keepWorkspaceAgentDockFocusOnPointer';

export type WorkspaceAgentSelectOptionButtonProps = {
  fullWidth: boolean;
  index: number;
  label: string;
  onOptionClick: (index: number) => void;
  selected: boolean;
};

/** A pick-one option: number badge, then a label that wraps instead of truncating. */
export const WorkspaceAgentSelectOptionButton = memo(
  ({ fullWidth, index, label, onOptionClick, selected }: WorkspaceAgentSelectOptionButtonProps) => {
    const handleClick = useCallback(() => {
      onOptionClick(index);
    }, [index, onOptionClick]);

    return (
      <button
        aria-pressed={selected}
        className={workspaceAgentChoiceChip({ fullWidth, selected, shape: 'option' })}
        data-option-index={index}
        data-testid="WORKSPACE_AGENT_SELECT_OPTION"
        onClick={handleClick}
        onMouseDown={keepWorkspaceAgentDockFocusOnPointer}
        type="button"
      >
        <span className="flex h-4 shrink-0 items-center">
          {index < WORKSPACE_AGENT_MAX_NUMBERED_OPTIONS ? (
            <span aria-hidden className={workspaceAgentChoiceChipNumber({ selected })}>
              {index + 1}
            </span>
          ) : null}
        </span>
        <span className="min-w-0 whitespace-normal break-words" data-option-label>
          {label}
        </span>
      </button>
    );
  },
);

WorkspaceAgentSelectOptionButton.displayName = 'WorkspaceAgentSelectOptionButton';
